// Centralized pricing configuration for AI Color Analysis
// All pricing values should be defined here to make updates easier

export const PRICING = {
  // Main AI Color Analysis pricing (in dollars)
  // Includes draping bundled in (no upsell)
  AI_COLOR_ANALYSIS: {
    // Settled 2026-07-17 from the ca-price-reverted-v1 test ($24.99 arm won on
    // revenue per visitor). Keep in sync with lib/priceExperiment.config.ts.
    BASE_PRICE: 24.99,
    BASE_PRICE_CENTS: 2499, // Stripe uses cents
  },

  // Advanced Draping pricing
  ADVANCED_DRAPING: {
    BASE_PRICE: 14,
    BASE_PRICE_CENTS: 1400,
  },

  // AI Hair Color Studio pricing
  HAIR_COLOR_CHANGER: {
    BASE_PRICE: 19.99,
    BASE_PRICE_CENTS: 1999,
  },

  // Kibbe Body Type Analysis pricing
  KIBBE_ANALYSIS: {
    BASE_PRICE: 14.99,
    BASE_PRICE_CENTS: 1499,
  },

  // AI Outfit Generator pricing
  OUTFIT_GENERATOR: {
    BASE_PRICE: 14.99,
    BASE_PRICE_CENTS: 1499,
    EXPANSION_PRICE: 9,
    EXPANSION_PRICE_CENTS: 900,
    // Bundle price for users who already purchased color analysis
    COLOR_BUNDLE_PRICE: 9,
    COLOR_BUNDLE_PRICE_CENTS: 900,
  },

  // Complete Style Profile Bundle pricing
  // Price varies based on how many dimensions already completed
  STYLE_PROFILE_BUNDLE: {
    // Full bundle (0 completed) - all 4 dimensions
    FULL_PRICE: 39,
    FULL_PRICE_CENTS: 3900,
    // 3 remaining dimensions (1 completed)
    THREE_REMAINING_PRICE: 28,
    THREE_REMAINING_PRICE_CENTS: 2800,
    // 2 remaining dimensions (2 completed)
    TWO_REMAINING_PRICE: 19,
    TWO_REMAINING_PRICE_CENTS: 1900,
    // 1 remaining dimension (3 completed) - same as individual add-on
    ONE_REMAINING_PRICE: 9,
    ONE_REMAINING_PRICE_CENTS: 900,
  },

  // Style Club subscription (one plan; monthly or annual)
  // Priced at/above the one-time analyses on purpose: membership INCLUDES the
  // $24.99 color analysis + $14.99 Kibbe analysis after trial conversion, so a
  // month must cost at least the one-time or it arbitrages the core product.
  STYLE_CLUB: {
    MONTHLY_PRICE: 24.99,
    MONTHLY_PRICE_CENTS: 2499,
    ANNUAL_PRICE: 199,
    ANNUAL_PRICE_CENTS: 19900,
    // Free trial for all new members (card required; charged after)
    TRIAL_DAYS: 7,
    // Stylist chat quota per member per calendar month
    CHAT_MESSAGES_PER_MONTH: 100,
  },

  // Upsell pricing
  UPSELLS: {
    COLOR_ANALYSIS_ADDON: {
      BASE_PRICE: 9,
      BASE_PRICE_CENTS: 900,
    },
    DRAPING_ADDON: {
      BASE_PRICE: 10,
      BASE_PRICE_CENTS: 1000,
    },
    // Color analysis add-on for outfit generator
    COLOR_ANALYSIS_FOR_OUTFITS: {
      BASE_PRICE: 9,
      BASE_PRICE_CENTS: 900,
    },
  },

  // Plan pricing (from constants/index.tsx)
  PLANS: {
    MINI_ARTIST: {
      PRICE: 1,
      CREDITS: 5,
    },
    CREATIVE_KID: {
      PRICE: 19,
      CREDITS: 50,
    },
    YOUNG_DA_VINCI: {
      PRICE: 15,
      CREDITS: 100,
    },
    MASTER_CRAFTER: {
      PRICE: 49,
      CREDITS: 500,
    },
    PICASSO: {
      PRICE: 80,
      CREDITS: 1000,
    },
  },
} as const;

// Regional (PPP-adjusted) pricing.
// `rate` is felt-price units per USD sticker dollar — NOT the FX rate. It's
// chosen so the main color-analysis product lands on a locally-reasonable
// price (e.g. India at the $24.99 sticker: rate 25 → ₹629, roughly a third of
// the ~₹2,100 FX conversion).
// Every SKU scales by the same rate and is rounded to a local charm price.
// `displayOriginalPrice` is the struck-through FX-equivalent anchor shown in
// the UI so the regional price reads as a discount.
export type RegionalPricing = {
  currency: string; // Stripe currency code
  symbol: string;
  rate: number;
  round: "charm9" | "thousand";
  displayOriginalPrice: string;
  /**
   * Approximate market FX units per USD. Used ONLY to enforce the global
   * minimum-charge floor, so precision doesn't matter — round generously
   * when updating.
   */
  fxPerUsd: number;
};

// HARD BUSINESS RULE (Mike, 2026-07-10): nothing is ever sold for less than
// $5 USD equivalent, anywhere in the world. PPP scaling below never takes a
// charge under this; toRegionalAmount raises floored prices to the nearest
// charm price at or above the floor.
export const MIN_CHARGE_USD = 5;

export const REGIONAL_PRICING: Record<string, RegionalPricing> = {
  // Low tier (~$8-9 felt price for the $24.99 product)
  IN: { currency: "inr", symbol: "₹", rate: 25, round: "charm9", displayOriginalPrice: "₹2,099", fxPerUsd: 84 }, // ₹629
  PK: { currency: "pkr", symbol: "₨", rate: 70, round: "charm9", displayOriginalPrice: "₨6,999", fxPerUsd: 280 }, // ₨1,749
  BD: { currency: "bdt", symbol: "৳", rate: 30, round: "charm9", displayOriginalPrice: "৳2,999", fxPerUsd: 120 }, // ৳749
  NP: { currency: "npr", symbol: "₨", rate: 40, round: "charm9", displayOriginalPrice: "₨3,399", fxPerUsd: 135 }, // ₨999
  ID: { currency: "idr", symbol: "Rp ", rate: 4450, round: "thousand", displayOriginalPrice: "Rp 399,000", fxPerUsd: 16000 }, // Rp 111,000
  PH: { currency: "php", symbol: "₱", rate: 17.5, round: "charm9", displayOriginalPrice: "₱1,449", fxPerUsd: 58 }, // ₱439
  // Mid tier (~$13-16 felt price)
  BR: { currency: "brl", symbol: "R$", rate: 2.5, round: "charm9", displayOriginalPrice: "R$139", fxPerUsd: 5.5 }, // R$59
  MX: { currency: "mxn", symbol: "MX$", rate: 10, round: "charm9", displayOriginalPrice: "MX$449", fxPerUsd: 18 }, // MX$249
};

export const getRegionalPricing = (country: string | null | undefined): RegionalPricing | null => {
  return (country && REGIONAL_PRICING[country]) || null;
};

// Convert a USD price in cents to the regional Stripe unit_amount (minor
// units). All currencies above are two-decimal in Stripe, so minor = x100.
// PPP-scaled prices at or above the $5 floor are unchanged; anything the
// scaling would take below it is raised to the nearest charm price ≥ floor.
export const toRegionalAmount = (usdCents: number, region: RegionalPricing): number => {
  const raw = (usdCents / 100) * region.rate;
  let rounded =
    region.round === "thousand"
      ? Math.max(1000, Math.round(raw / 1000) * 1000)
      : Math.max(9, Math.round((raw + 1) / 10) * 10 - 1);
  const floor = MIN_CHARGE_USD * region.fxPerUsd;
  if (rounded < floor) {
    rounded =
      region.round === "thousand"
        ? Math.ceil(floor / 1000) * 1000
        : Math.ceil((floor + 1) / 10) * 10 - 1; // smallest x…9 ≥ floor
  }
  return rounded * 100;
};

export const formatRegionalPrice = (usdCents: number, region: RegionalPricing): string => {
  const units = toRegionalAmount(usdCents, region) / 100;
  return `${region.symbol}${units.toLocaleString("en-US")}`;
};

// Helper functions for common pricing operations
export const getPriceInCents = (priceInDollars: number): number => {
  return priceInDollars * 100;
};

export const getPriceInDollars = (priceInCents: number): number => {
  return priceInCents / 100;
};

// Type for pricing tiers
export type PricingTier = {
  id: string;
  title: string;
  price: number;
  originalPrice?: number;
  description: string;
  features: {
    category: string;
    items: string[];
  }[];
  turnaround: string;
  recommended?: boolean;
  photo?: string;
};

// Default pricing tiers using centralized pricing
export const DEFAULT_PRICING_TIERS: PricingTier[] = [
  {
    id: "full",
    title: "Professional",
    price: PRICING.AI_COLOR_ANALYSIS.BASE_PRICE,
    description: "Complete style transformation with casual and professional looks",
    features: [
      {
        category: "🎨 Color Analysis",
        items: ["Colors that you should wear and avoid", "Color palette guide"],
      },
      {
        category: "💄 Makeup",
        items: ["Makeup guide (lipstick, eyes, blush)"],
      },
      {
        category: "💫 We'll digitally render previews of you in your season:",
        items: ["8 casual outfits", "4 professional outfits", "2 formal outfits"],
      },
    ],
    turnaround: "Get your analysis in 5 minutes",
  },
];
